import { useEffect, useState } from 'react';
import {
  CUSTOM_LICENSE_BODY,
  LICENSE_BODY_NAME_MAX,
  LICENSE_BODY_OPTIONS,
  OTHER_LICENSE_BODIES,
  OTHER_LICENSE_SENTINEL,
  isPrimaryLicenseBody,
} from '@lighthouse/shared';

export function LicenseBodiesField({
  bodies,
  onChange,
  variant = 'plain',
}: {
  bodies: string[];
  onChange: (bodies: string[]) => void;
  variant?: 'plain' | 'chips';
}) {
  const primaries = bodies.filter((id) => isPrimaryLicenseBody(id));
  const extra = bodies.find((id) => id !== OTHER_LICENSE_SENTINEL && !isPrimaryLicenseBody(id));
  const otherOn = bodies.includes(OTHER_LICENSE_SENTINEL) || Boolean(extra);
  const knownSelected = OTHER_LICENSE_BODIES.some((body) => body.id === extra) ? (extra ?? '') : '';
  const customText = extra && !knownSelected ? extra : '';
  const [showCustom, setShowCustom] = useState(Boolean(customText));

  useEffect(() => {
    if (customText) setShowCustom(true);
  }, [customText]);

  const dropdownValue = knownSelected || (showCustom || customText ? CUSTOM_LICENSE_BODY : '');

  const emit = (nextPrimaries: string[], nextExtra: string | null) => {
    const next = [...nextPrimaries];
    if (nextExtra) next.push(nextExtra);
    if (next.length === 0) return;
    onChange(next);
  };

  const togglePrimary = (id: string) => {
    const has = primaries.includes(id);
    const nextPrimaries = has ? primaries.filter((item) => item !== id) : [...primaries, id];
    const nextExtra = otherOn ? knownSelected || customText || OTHER_LICENSE_SENTINEL : null;
    emit(nextPrimaries, nextExtra);
  };

  const setOther = (checked: boolean) => {
    if (!checked) {
      setShowCustom(false);
      emit(primaries, null);
      return;
    }
    emit(primaries, knownSelected || customText || OTHER_LICENSE_SENTINEL);
  };

  const setKnown = (value: string) => {
    if (value === CUSTOM_LICENSE_BODY) {
      setShowCustom(true);
      emit(primaries, customText || OTHER_LICENSE_SENTINEL);
      return;
    }
    setShowCustom(false);
    emit(primaries, value || OTHER_LICENSE_SENTINEL);
  };

  return (
    <div>
      <div className={variant === 'chips' ? 'flex flex-wrap gap-2' : 'mt-2 flex flex-wrap gap-3'}>
        {LICENSE_BODY_OPTIONS.map((body) => {
          const checked =
            body.id === OTHER_LICENSE_SENTINEL ? otherOn : primaries.includes(body.id);
          return (
            <label
              key={body.id}
              className={
                variant === 'chips'
                  ? `flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
                      checked
                        ? 'border-primary-600 bg-glacier text-stone-900'
                        : 'border-stone-300 bg-white text-stone-700'
                    }`
                  : 'flex items-center gap-2 text-sm text-gray-700'
              }
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() =>
                  body.id === OTHER_LICENSE_SENTINEL ? setOther(!checked) : togglePrimary(body.id)
                }
              />
              {body.label}
            </label>
          );
        })}
      </div>
      {otherOn ? (
        <div className="mt-3 max-w-md space-y-2">
          <label className="label" htmlFor="other-license-body">
            Recognised body
          </label>
          <select
            id="other-license-body"
            className="select-field"
            value={dropdownValue}
            onChange={(event) => setKnown(event.target.value)}
          >
            <option value="">Select a body</option>
            {OTHER_LICENSE_BODIES.map((body) => (
              <option key={body.id} value={body.id}>
                {body.label}
              </option>
            ))}
            <option value={CUSTOM_LICENSE_BODY}>Not listed — type the name</option>
          </select>
          {dropdownValue === CUSTOM_LICENSE_BODY ? (
            <input
              className="input-field"
              value={customText}
              maxLength={LICENSE_BODY_NAME_MAX}
              placeholder="Name of the licensing body"
              aria-label="Licensing body name"
              onChange={(event) => {
                const value = event.target.value;
                emit(primaries, value.trim() ? value : OTHER_LICENSE_SENTINEL);
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
